import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Home, 
  Package, 
  ShoppingBag, 
  LogOut, 
  Plus, 
  ScanBarcode, 
  Database, 
  History,
  HelpCircle,
  Menu,
  ChevronDown,
  BarChart3,
  Settings,
  Bell,
  Building2,
  Receipt,
  FileText,
  Webhook,
  Leaf
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../Logo';
import { drlpNotifications } from '../../utils/api';
// NOTE: LanguageSwitcher temporarily disabled for Retailer app (English-only mode)
// To re-enable: uncomment the import and add <LanguageSwitcher variant="compact" /> in the header
// import LanguageSwitcher from '../LanguageSwitcher';
import { useLanguage } from '../../contexts/LanguageContext';
import GuidedWalkthrough, { useGuidedWalkthrough } from './GuidedWalkthrough';

export default function RetailerLayout({ children, user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Tutorial state - NO auto-show, only manual trigger
  const { isOpen: tutorialOpen, openWalkthrough, closeWalkthrough } = useGuidedWalkthrough();

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await drlpNotifications.unreadCount();
        setUnreadCount(response.data.unread_count || 0);
      } catch (error) {
        // Silently fail - notifications are not critical
        console.debug('Failed to fetch notification count');
      }
    };

    fetchUnreadCount();
    // Poll every 30 seconds for new notifications
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const isTeamMember = user?.role_in_team === 'team_member';
  const isCorporate = user?.account_type === 'corporate' || user?.role_in_team === 'corporate_admin';

  const allNavItems = [
    { path: '/retailer/dashboard', icon: Home, label: t('retailer.nav.dashboard', 'Dashboard'), testId: 'retailer-nav-home' },
    { path: '/retailer/scan', icon: ScanBarcode, label: t('retailer.nav.scan', 'Scan'), testId: 'retailer-nav-scan', highlight: true },
    { path: '/retailer/post-item', icon: Plus, label: t('retailer.nav.post_deal', 'Post Deal'), testId: 'retailer-nav-post' },
    { path: '/retailer/inventory', icon: Package, label: t('retailer.nav.inventory', 'Inventory'), testId: 'retailer-nav-inventory' },
    { path: '/retailer/orders', icon: ShoppingBag, label: t('retailer.nav.orders', 'Orders'), testId: 'retailer-nav-orders' },
    { path: '/retailer/impact', icon: Leaf, label: 'Impact', testId: 'retailer-nav-impact' },
    { path: '/retailer/settlements', icon: Receipt, label: 'Settlements', testId: 'retailer-nav-settlements' },
    { path: '/retailer/reconciliation', icon: FileText, label: 'Tax Logs', testId: 'retailer-nav-reconciliation' },
    { path: '/retailer/webhooks', icon: Webhook, label: 'Push Delivery', testId: 'retailer-nav-webhooks' },
    { path: '/retailer/analytics', icon: BarChart3, label: t('retailer.nav.analytics', 'Analytics'), testId: 'retailer-nav-analytics', leadOnly: true },
    { path: '/retailer/corporate', icon: Building2, label: 'Corporate', testId: 'retailer-nav-corporate', corporateOnly: true },
    { path: '/retailer/settings', icon: Settings, label: t('retailer.nav.settings', 'Settings'), testId: 'retailer-nav-settings', leadOnly: true },
    { path: '/retailer/history', icon: History, label: t('retailer.nav.history', 'History'), testId: 'retailer-nav-history' },
    { path: '/retailer/store-codes', icon: Database, label: t('retailer.nav.store_codes', 'Codes'), testId: 'retailer-nav-codes' },
    { path: '/retailer/guide', icon: HelpCircle, label: 'Guide', testId: 'retailer-nav-guide' },
  ];
  const navItems = allNavItems.filter((it) => {
    if (isTeamMember && it.leadOnly) return false;
    if (it.corporateOnly && !isCorporate) return false;
    return true;
  });

  const handleTutorialClick = () => {
    setMobileMenuOpen(false);
    openWalkthrough();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div 
              className="cursor-pointer"
              onClick={() => navigate('/retailer/dashboard')}
              data-testid="retailer-logo-home"
            >
              <Logo size="default" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700">{t('retailer.header.portal', 'DealShaq Grocer App')}</p>
              <p className="text-xs text-gray-500">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/retailer/orders')}
              className="relative text-gray-600 hover:text-gray-900"
              data-testid="retailer-notifications-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-semibold">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
            {/* Language selector disabled - Retailer app is English-only for now */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              data-testid="retailer-logout-btn"
              className="text-gray-600 hover:text-gray-900"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{t('common.logout', 'Logout')}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation - Desktop (horizontal) */}
      <nav className="bg-white border-b hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex space-x-1">
            {(navItems || []).map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={item.testId}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-blue-600 text-blue-600'
                      : item.highlight && !isActive
                      ? 'border-transparent text-green-600 hover:text-green-700'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <item.icon className={`w-4 h-4 flex-shrink-0 ${item.highlight && !isActive ? 'text-green-500' : ''}`} />
                  <span className="text-sm font-medium">{item.label}</span>
                  {item.highlight && !isActive && (
                    <span className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-600 rounded-full font-semibold">
                      NEW
                    </span>
                  )}
                </button>
              );
            })}
            {/* Tutorial Button - Desktop */}
            <button
              data-testid="retailer-nav-tutorial"
              onClick={handleTutorialClick}
              className="flex items-center space-x-2 px-4 py-3 border-b-2 border-transparent text-purple-600 hover:text-purple-700 hover:border-purple-300 transition-colors whitespace-nowrap"
            >
              <HelpCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium">{t('retailer.nav.tutorial', 'Tutorial')}</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Navigation - Mobile (dropdown) */}
      <nav className="bg-white border-b md:hidden">
        <div className="container mx-auto px-4">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="flex items-center justify-between w-full py-3 text-gray-700"
            data-testid="retailer-mobile-menu-toggle"
          >
            <div className="flex items-center space-x-2">
              <Menu className="w-5 h-5" />
              <span className="font-medium">
                {(navItems || []).find(item => item.path === location.pathname)?.label || 'Menu'}
              </span>
            </div>
            <ChevronDown className={`w-5 h-5 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {/* Mobile Dropdown Menu */}
          {mobileMenuOpen && (
            <div className="absolute left-0 right-0 bg-white border-b shadow-lg z-40">
              <div className="container mx-auto px-4 py-2">
                {(navItems || []).map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      data-testid={`mobile-${item.testId}`}
                      onClick={() => {
                        navigate(item.path);
                        setMobileMenuOpen(false);
                      }}
                      className={`flex items-center space-x-3 w-full px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-600'
                          : item.highlight
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      <span className="font-medium">{item.label}</span>
                      {item.highlight && !isActive && (
                        <span className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-600 rounded-full font-semibold ml-auto">
                          NEW
                        </span>
                      )}
                    </button>
                  );
                })}
                {/* Tutorial Button - Mobile */}
                <button
                  data-testid="mobile-retailer-nav-tutorial"
                  onClick={handleTutorialClick}
                  className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-purple-600 hover:bg-purple-50 transition-colors"
                >
                  <HelpCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium">{t('retailer.nav.tutorial', 'Tutorial')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <main className="container mx-auto px-4 py-6">{children}</main>
      
      {/* Tutorial Walkthrough - Only shows when manually triggered */}
      <GuidedWalkthrough 
        isOpen={tutorialOpen}
        onClose={closeWalkthrough}
        onComplete={closeWalkthrough}
      />
    </div>
  );
}
